export const APP_LANGUAGES = [
  { code: "en", nativeName: "English", flag: "🇬🇧" },
  { code: "fr", nativeName: "Français", flag: "🇫🇷" },
  { code: "de", nativeName: "Deutsch", flag: "🇩🇪" },
  { code: "es", nativeName: "Español", flag: "🇪🇸" },
  { code: "nl", nativeName: "Nederlands", flag: "🇳🇱" },
  { code: "pt", nativeName: "Português", flag: "🇵🇹" },
];

export function normalizeAppLang(raw) {
  if (!raw || typeof raw !== "string") return "en";
  const two = raw.trim().split("-")[0].toLowerCase();
  if (two === "gr") return "de";
  if (two === "sp") return "es";
  return APP_LANGUAGES.some((l) => l.code === two) ? two : "en";
}

export function currentAppLanguage(i18n) {
  return normalizeAppLang(
    storedAppLangRaw() || i18n?.language
  );
}

/**
 * Folder / S3 language for baked tour clips.
 * i18n uses de/es; audio files and Gemini cache keys live under gr/sp.
 */
export function tourClipLang(raw) {
  const code = normalizeAppLang(raw);
  if (code === "de") return "gr";
  if (code === "es") return "sp";
  return code;
}

/** Raw stored UI language, or null when the user has not chosen one this device. */
export function storedAppLangRaw() {
  try {
    const raw = localStorage.getItem("lng") || localStorage.getItem("i18nextLng");
    if (!raw || typeof raw !== "string") return null;
    const two = raw.trim().split("-")[0].toLowerCase();
    if (two === "gr") return "de";
    if (two === "sp") return "es";
    return APP_LANGUAGES.some((l) => l.code === two) ? two : null;
  } catch {
    return null;
  }
}

/**
 * Language to keep after login / Digiping: device choice from profiling first,
 * then the account's saved user_lang (so a default DB "en" does not wipe FR/NL).
 */
export function preferredAppLanguage(user, i18n) {
  const stored = storedAppLangRaw();
  if (stored) return stored;
  if (user?.user_lang) return normalizeAppLang(user.user_lang);
  if (i18n?.language) return normalizeAppLang(i18n.language);
  return "en";
}

export function appLanguageMeta(code) {
  const lang = normalizeAppLang(code);
  return APP_LANGUAGES.find((l) => l.code === lang) || APP_LANGUAGES[0];
}

const APP_LANG_ALIASES = {
  en: "en",
  english: "en",
  anglais: "en",
  fr: "fr",
  french: "fr",
  francais: "fr",
  français: "fr",
  de: "de",
  german: "de",
  allemand: "de",
  deutsch: "de",
  gr: "de",
  es: "es",
  spanish: "es",
  espagnol: "es",
  espanol: "es",
  español: "es",
  castellano: "es",
  sp: "es",
  nl: "nl",
  dutch: "nl",
  neerlandais: "nl",
  néerlandais: "nl",
  nederlands: "nl",
  vlaams: "nl",
  flemish: "nl",
  pt: "pt",
  portuguese: "pt",
  portugais: "pt",
  portugues: "pt",
  português: "pt",
};

export function parseRequestedAppLang(raw) {
  const key = String(raw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  if (!key) return null;
  if (APP_LANG_ALIASES[key]) return APP_LANG_ALIASES[key];
  const two = key.slice(0, 2);
  return APP_LANGUAGES.some((l) => l.code === two) ? two : null;
}

/** Apply UI language locally (storage + i18n). Does not call the API. */
export function applyAppLanguage(i18n, lng) {
  const code = normalizeAppLang(lng);
  try {
    localStorage.setItem("lng", code);
    localStorage.setItem("i18nextLng", code);
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user && typeof user === "object") {
      user.user_lang = code;
      localStorage.setItem("user", JSON.stringify(user));
    }
  } catch {
    /* ignore */
  }
  const current = normalizeAppLang(i18n?.language);
  if (current !== code) i18n?.changeLanguage?.(code);
  return code;
}
